/**
 * Word document import (docx -> editable Markdown draft).
 *
 * The pipeline runs entirely in the browser, in this order:
 *
 *   1. `parseDocx` — mammoth turns the .docx into clean semantic HTML.
 *      Images arrive as inline `data:` URLs.
 *   2. `replaceDataImages` — every embedded image is uploaded through the
 *      media API (bytes to Git, AGENTS.md §13), and its `src` rewritten to the
 *      public URL. A base64 blob must never reach the draft: the Markdown
 *      serializer drops `data:` URLs (safeUrl), so an un-uploaded image would
 *      silently vanish at the first save.
 *   3. `htmlToMarkdown` (@blog/editor) — the editor's own pipeline turns the
 *      HTML into the same Markdown a typed draft would have.
 *
 * The .docx itself is an import source, not an asset: it is not stored, so it
 * is deliberately not part of the media allow-list (SPEC §18).
 */

export interface ParsedDocx {
  /** Semantic HTML fragment (headings, paragraphs, lists, `data:` images). */
  readonly html: string;
  /** Human-readable notes about features mammoth could not carry over. */
  readonly warnings: readonly string[];
}

/** Convert a .docx file's bytes into HTML. */
export async function parseDocx(bytes: ArrayBuffer): Promise<ParsedDocx> {
  // Lazy so mammoth stays out of the main bundle; importing a Word file is a
  // rare action, and the rest of the Admin should not pay for it.
  const mammoth = await import("mammoth");
  // The browser build reads `arrayBuffer`, the Node build (tests) `buffer` —
  // each runtime gets the key it knows, so app and tests share one code path.
  const input: { arrayBuffer: ArrayBuffer; buffer?: Uint8Array } = { arrayBuffer: bytes };

  if (typeof Buffer !== "undefined") {
    input.buffer = Buffer.from(bytes);
  }

  const result = await mammoth.convertToHtml(input);

  return {
    html: result.value,
    warnings: result.messages.map((message) => message.message),
  };
}

/**
 * The article title mammoth's HTML suggests: the first heading's text.
 *
 * Pure string handling rather than `DOMParser`, so it is testable in Node; the
 * input is mammoth's output, which is machine-generated and predictable.
 * Returns `null` when the document has no heading — the caller falls back to
 * the filename.
 */
export function extractTitleFromHtml(html: string): string | null {
  const heading = /<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i.exec(html);

  if (heading === null) {
    return null;
  }

  const text = decodeEntities((heading[1] ?? "").replace(/<[^>]*>/g, ""))
    .replace(/\s+/g, " ")
    .trim();

  return text === "" ? null : text;
}

/** Unescape the five entities mammoth's output can contain. */
function decodeEntities(text: string): string {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/**
 * Remove the document's own title heading from the body.
 *
 * The first heading becomes the frontmatter `title`; keeping it in the content
 * too would show the article twice in a row. Only an exact-text match is
 * removed — a different heading is content and stays.
 */
export function stripLeadingTitleHeading(html: string, title: string): string {
  if (title === "") {
    return html;
  }

  const match = /^(\s*)<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i.exec(html);

  if (match === null) {
    return html;
  }

  const headingText = decodeEntities((match[2] ?? "").replace(/<[^>]*>/g, ""))
    .replace(/\s+/g, " ")
    .trim();

  // `match[0]` already spans any leading whitespace.
  return headingText === title.trim() ? html.slice(match[0].length) : html;
}

/** Parse a `data:` URL into its MIME type and Base64 payload. */
function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } | null {
  const match = /^data:([^;,]+);base64,([\s\S]+)$/.exec(dataUrl.trim());

  if (match === null) {
    return null;
  }

  return { mimeType: match[1] ?? "", base64: match[2] ?? "" };
}

const EXTENSION_BY_IMAGE_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

/**
 * Turn an embedded image's `data:` URL into an uploadable `File`.
 *
 * Returns `null` for anything that is not a base64 image data URL — the media
 * API's allow-list would reject it anyway, so it never becomes a request.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File | null {
  const parsed = parseDataUrl(dataUrl);

  if (parsed === null || !parsed.mimeType.startsWith("image/")) {
    return null;
  }

  const binary = atob(parsed.base64.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  const extension = EXTENSION_BY_IMAGE_MIME[parsed.mimeType] ?? "png";
  const name = filename.includes(".") ? filename : `${filename}.${extension}`;

  return new File([bytes], name, { type: parsed.mimeType });
}

/** Uploads one embedded image and returns its public URL, or `null` to skip it. */
export type ImageUploader = (file: File) => Promise<string | null>;

export interface ImageRewriteResult {
  /** The HTML with every successfully uploaded `src` rewritten. */
  readonly html: string;
  readonly uploaded: number;
  /** Images that could not be uploaded and remain as `data:` URLs. */
  readonly failed: number;
}

/**
 * Upload every embedded image and rewrite the HTML against the public URLs.
 *
 * A failed upload leaves that image untouched rather than dropping it: the
 * serializer keeps its alt text, so the content survives even if the picture
 * does not, and the author can see what needs re-attaching.
 */
export async function replaceDataImages(
  html: string,
  upload: ImageUploader,
): Promise<ImageRewriteResult> {
  const document = new DOMParser().parseFromString(html, "text/html");
  const images = Array.from(document.querySelectorAll("img[src^='data:']"));

  let uploaded = 0;
  let failed = 0;

  for (const [index, image] of images.entries()) {
    const src = image.getAttribute("src") ?? "";
    const fallbackName = image.getAttribute("alt")?.trim() || `image-${index + 1}`;
    const file = dataUrlToFile(src, fallbackName);

    if (file === null) {
      failed += 1;
      continue;
    }

    const url = await upload(file);

    if (url === null) {
      failed += 1;
      continue;
    }

    image.setAttribute("src", url);
    uploaded += 1;
  }

  return { html: document.body.innerHTML, uploaded, failed };
}
